import { suggestDevice } from "../devices/catalog";
import {
  type DeviceProps,
  type DeviceScreenQuad,
  deviceAspect,
  deviceExtent,
  deviceScreenQuad,
} from "../devices/layout";
import type { Rect } from "../scene/geometry";
import type { ClipDevice, OverlayBox, OverlayClip } from "../scene/schema";
import type { Size } from "./framing";

/**
 * Clips inside a device mockup (screen recordings in a phone, laptop or
 * browser window; docs/19-mockups.md). The clip's box holds the whole device,
 * centred like a device element; the picture plays on the screen. The editor
 * preview (CSS transforms) and the export (ffmpeg `perspective`) both place
 * the screen with `deviceScreenQuad`, so they match.
 */

/** Picture aspect (height / width) for the device layout: browser windows take the video's shape. */
export function clipContentAspect(
  source: { width?: number | null; height?: number | null } | null | undefined,
): number | null {
  return source?.width && source.height ? source.height / source.width : null;
}

/** What the device layout needs of a clip's device settings. */
export function clipDeviceProps(device: ClipDevice): DeviceProps {
  return {
    device: device.device,
    ...(device.finish ? { finish: device.finish } : {}),
    landscape: device.landscape,
    tilt: device.tilt,
    ...(device.url ? { url: device.url } : {}),
  };
}

/** Device settings for a clip of this picture size (the matching phone, tablet, laptop …). */
export function suggestClipDevice(
  width: number | null | undefined,
  height: number | null | undefined,
): ClipDevice {
  const suggestion = suggestDevice(width, height);
  return {
    device: suggestion.device,
    landscape: suggestion.landscape,
    tilt: { x: 0, y: 0, z: 0 },
    glare: false,
  };
}

/** Share of the frame a device takes when its box filled the whole frame. */
const FULL_FRAME_SHARE = 0.86;

const round = (box: Rect): OverlayBox => ({
  x: Math.round(box.x),
  y: Math.round(box.y),
  width: Math.max(10, Math.round(box.width)),
  height: Math.max(10, Math.round(box.height)),
});

/** A box of `aspect` (width / height) around `center`, shrunk to fit into the frame. */
function boxAround(
  center: { x: number; y: number },
  size: Size,
  aspect: number,
  frame: Size,
): { x: number; y: number; width: number; height: number } {
  let { width, height } = size;
  const shrink = Math.min(1, frame.width / width, frame.height / height);
  width *= shrink;
  height = width / aspect;
  return { x: center.x - width / 2, y: center.y - height / 2, width, height };
}

/**
 * Box for showing a clip in a device: the largest box of the device's
 * `aspect` (width / height) inside `box`, centred, and inside the frame. A
 * box covering (nearly) the whole frame leaves a margin around the device.
 */
export function fitDeviceBox(box: Rect, aspect: number, frame: Size): OverlayBox {
  const left = Math.max(0, box.x);
  const top = Math.max(0, box.y);
  const right = Math.min(frame.width, box.x + box.width);
  const bottom = Math.min(frame.height, box.y + box.height);
  const area =
    right - left >= 10 && bottom - top >= 10
      ? { x: left, y: top, width: right - left, height: bottom - top }
      : { x: 0, y: 0, width: frame.width, height: frame.height };
  const full = area.width >= frame.width * 0.95 && area.height >= frame.height * 0.95;
  const share = full ? FULL_FRAME_SHARE : 1;
  const width = Math.min(area.width, area.height * aspect) * share;
  const height = width / aspect;
  return round({
    x: area.x + (area.width - width) / 2,
    y: area.y + (area.height - height) / 2,
    width,
    height,
  });
}

/**
 * The box after the device settings change (other model, turned, another
 * view): around the same centre. A new view of the same device keeps its
 * size on screen (millimetres per pixel), another device keeps the area. A
 * box that was inside the frame stays inside.
 */
export function refitDeviceBox(
  box: Rect,
  before: ClipDevice | undefined,
  after: ClipDevice,
  contentAspect: number | null,
  frame: Size,
): OverlayBox {
  const center = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
  const next = clipDeviceProps(after);
  const aspect = deviceAspect(next, contentAspect);
  let size: Size;
  if (before && before.device === after.device && before.landscape === after.landscape) {
    const old = deviceExtent(clipDeviceProps(before), contentAspect);
    const scale = Math.min(box.width / old.width, box.height / old.height);
    const extent = deviceExtent(next, contentAspect);
    size = { width: extent.width * scale, height: extent.height * scale };
  } else {
    const width = Math.sqrt(box.width * box.height * aspect);
    size = { width, height: width / aspect };
  }
  const result = boxAround(center, size, aspect, frame);
  const inside =
    box.x >= -0.5 &&
    box.y >= -0.5 &&
    box.x + box.width <= frame.width + 0.5 &&
    box.y + box.height <= frame.height + 0.5;
  if (inside) {
    result.x = Math.min(Math.max(0, result.x), frame.width - result.width);
    result.y = Math.min(Math.max(0, result.y), frame.height - result.height);
  }
  return round(result);
}

/** Screen of a device clip in document px (the corners on the frame), or null. */
export function deviceClipScreen(
  clip: Pick<OverlayClip, "box" | "device">,
  source: { width?: number | null; height?: number | null } | null | undefined,
): DeviceScreenQuad | null {
  if (!clip.device) return null;
  const quad = deviceScreenQuad(
    clipDeviceProps(clip.device),
    { width: clip.box.width, height: clip.box.height },
    clipContentAspect(source),
  );
  if (!quad) return null;
  const move = (point: { x: number; y: number }) => ({
    x: point.x + clip.box.x,
    y: point.y + clip.box.y,
  });
  return {
    ...quad,
    topLeft: move(quad.topLeft),
    topRight: move(quad.topRight),
    bottomRight: move(quad.bottomRight),
    bottomLeft: move(quad.bottomLeft),
  };
}
